import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dumbbell, Plus, Edit, Trash2, Search, X, ChevronDown, ChevronUp,
  Eye, Layers, Clock, Target, Crown, RefreshCw, Copy, CheckSquare, Square, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import WorkoutForm from "./WorkoutForm";
import { useViewPreferences, ViewControls, sortItems, BulkDeleteModal, SelectionHeader, API } from "./SharedComponents";
import { SubtabManager, SubtabSelector } from "./SubtabManager";

// ==================== WORKOUT LIBRARY ====================
const WorkoutLibrary = () => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const { viewMode, setViewMode, sortMode, setSortMode } = useViewPreferences("workouts");
  
  // Filter state - category tabs
  const [categoryFilter, setCategoryFilter] = useState("");
  const [timesPerWeekFilter, setTimesPerWeekFilter] = useState("");
  const [subtabFilter, setSubtabFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Workout categories (matching your 6 S's)
  const workoutCategories = [
    { value: "", label: "All" },
    { value: "speed", label: "Speed" },
    { value: "strength", label: "Strength" },
    { value: "stamina", label: "Stamina" },
    { value: "suppleness", label: "Suppleness" },
    { value: "skill", label: "Skill" },
    { value: "extras", label: "Extras" },
  ];
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchWorkouts();
  }, []);

  const fetchWorkouts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/coach/workout-templates`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setWorkouts(data.templates);
      }
    } catch (error) {
      toast.error("Failed to load workouts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workoutId) => {
    if (!confirm("Are you sure you want to delete this workout?")) return;
    try {
      const response = await fetch(`${API}/coach/workout-templates/${workoutId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Workout deleted");
        setSelectedIds(prev => { const next = new Set(prev); next.delete(workoutId); return next; });
        fetchWorkouts();
      }
    } catch (error) {
      toast.error("Failed to delete workout");
    }
  };

  const handleDuplicate = async (workoutId) => {
    try {
      const response = await fetch(`${API}/coach/workout-templates/${workoutId}/duplicate`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Workout duplicated!");
        fetchWorkouts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to duplicate workout");
      }
    } catch (error) {
      toast.error("Failed to duplicate workout");
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === workouts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(workouts.map(w => w.workout_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const response = await fetch(`${API}/coach/workout-templates/${id}`, {
          method: "DELETE",
          credentials: "include"
        });
        if (response.ok) successCount++;
      } catch (error) {
        console.error(`Failed to delete workout ${id}:`, error);
      }
    }
    setBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedIds(new Set());
    toast.success(`Deleted ${successCount} workout${successCount !== 1 ? 's' : ''}`);
    fetchWorkouts();
  };

  const selectedWorkouts = workouts.filter(w => selectedIds.has(w.workout_id));
  
  // Filter workouts by category, times per week, subtab, and search
  const filteredWorkouts = React.useMemo(() => {
    return workouts.filter(workout => {
      const matchesCategory = !categoryFilter || workout.category === categoryFilter;
      const matchesTPW = !timesPerWeekFilter || workout.times_per_week === parseInt(timesPerWeekFilter);
      const matchesSubtab = !subtabFilter || (workout.subtabs && workout.subtabs.includes(subtabFilter));
      const matchesSearch = !searchQuery || 
        workout.title?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesTPW && matchesSubtab && matchesSearch;
    });
  }, [workouts, categoryFilter, timesPerWeekFilter, subtabFilter, searchQuery]);

  // Clear subtab and times per week filter when category changes
  const handleCategoryChange = (newCategory) => {
    setCategoryFilter(newCategory);
    setSubtabFilter(""); // Reset subtab when category changes
    setTimesPerWeekFilter(""); // Reset times per week when category changes
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Workout Library</h2>
          <p className="text-gray-400">
            {(categoryFilter || subtabFilter || timesPerWeekFilter || searchQuery)
              ? `Showing ${filteredWorkouts.length} of ${workouts.length} workouts`
              : `Build workouts from your exercise library`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <ViewControls 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            sortMode={sortMode}
            setSortMode={setSortMode}
          />
          <button
            onClick={() => { setEditingWorkout(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            data-testid="create-workout-btn"
          >
            <Plus className="w-5 h-5" />
            Create Workout
          </button>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {workoutCategories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => handleCategoryChange(cat.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              categoryFilter === cat.value
                ? "bg-primary text-white"
                : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
            }`}
            data-testid={`filter-category-${cat.value || 'all'}`}
          >
            {cat.label}
          </button>
        ))}
        
        {/* Search */}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search workouts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 bg-[#1E293B] border border-[#1E293B] rounded-lg text-sm focus:border-primary outline-none w-48"
            data-testid="search-workouts"
          />
        </div>
      </div>

      {/* Times Per Week Filter Pills */}
      {categoryFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setTimesPerWeekFilter("")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !timesPerWeekFilter
                ? "bg-[#374B69] text-white"
                : "bg-[#141B2D] text-gray-400 hover:bg-[#2D3B4F]"
            }`}
            data-testid="tpw-filter-all"
          >
            All
          </button>
          {[2, 3, 4, 5, 6].map((tpw) => {
            const colors = {
              2: { dot: "bg-green-500", active: "bg-green-500 text-white", inactive: "bg-green-500/20 text-green-400" },
              3: { dot: "bg-blue-500", active: "bg-blue-500 text-white", inactive: "bg-blue-500/20 text-blue-400" },
              4: { dot: "bg-yellow-500", active: "bg-yellow-500 text-black", inactive: "bg-yellow-500/20 text-yellow-400" },
              5: { dot: "bg-orange-500", active: "bg-orange-500 text-white", inactive: "bg-orange-500/20 text-orange-400" },
              6: { dot: "bg-red-500", active: "bg-red-500 text-white", inactive: "bg-red-500/20 text-red-400" },
            };
            const c = colors[tpw];
            const isActive = timesPerWeekFilter === tpw.toString();
            return (
              <button
                key={tpw}
                onClick={() => setTimesPerWeekFilter(isActive ? "" : tpw.toString())}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive ? c.active : `${c.inactive} hover:bg-[#2D3B4F]`
                }`}
                data-testid={`tpw-filter-${tpw}`}
              >
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                {tpw}x/week
              </button>
            );
          })}
        </div>
      )}

      {/* Subtabs (only shown when a category is selected) */}
      <SubtabManager
        libraryType="workouts"
        selectedCategory={categoryFilter}
        selectedSubtab={subtabFilter}
        onSubtabSelect={setSubtabFilter}
      />

      {/* Selection Header */}
      {filteredWorkouts.length > 0 && (
        <SelectionHeader
          selectedCount={selectedIds.size}
          totalCount={filteredWorkouts.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onDeleteSelected={() => setShowBulkDeleteModal(true)}
          allSelected={selectedIds.size === workouts.length && workouts.length > 0}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : filteredWorkouts.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortItems(filteredWorkouts, sortMode, "title").map((workout) => (
              <WorkoutCard
                key={workout.workout_id}
                workout={workout}
                selected={selectedIds.has(workout.workout_id)}
                onToggleSelect={() => toggleSelect(workout.workout_id)}
                onView={() => setViewingWorkout(workout)}
                onEdit={() => { setEditingWorkout(workout); setShowForm(true); }}
                onDuplicate={() => handleDuplicate(workout.workout_id)}
                onDelete={() => handleDelete(workout.workout_id)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {sortItems(filteredWorkouts, sortMode, "title").map((workout) => (
              <WorkoutListItem
                key={workout.workout_id}
                workout={workout}
                selected={selectedIds.has(workout.workout_id)}
                onToggleSelect={() => toggleSelect(workout.workout_id)}
                onView={() => setViewingWorkout(workout)}
                onEdit={() => { setEditingWorkout(workout); setShowForm(true); }}
                onDuplicate={() => handleDuplicate(workout.workout_id)}
                onDelete={() => handleDelete(workout.workout_id)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-[#141B2D] border border-[#1E293B] rounded-xl">
          <Layers className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">
            {(categoryFilter || subtabFilter || timesPerWeekFilter || searchQuery) ? `No workouts match your filters (${workouts.length} total)` : "No workouts created yet"}
          </p>
          {(categoryFilter || subtabFilter || timesPerWeekFilter || searchQuery) && (
            <button
              onClick={() => { setCategoryFilter(""); setSubtabFilter(""); setTimesPerWeekFilter(""); setSearchQuery(""); }}
              className="mt-3 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
              data-testid="clear-all-filters-btn"
            >
              Clear All Filters
            </button>
          )}
          {!categoryFilter && !subtabFilter && !timesPerWeekFilter && !searchQuery && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-primary hover:underline"
            >
              Create your first workout
            </button>
          )}
        </div>
      )}

      {/* Bulk Delete Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <BulkDeleteModal
            items={selectedWorkouts}
            itemType="Workout"
            nameField="title"
            onConfirm={handleBulkDelete}
            onCancel={() => setShowBulkDeleteModal(false)}
            deleting={bulkDeleting}
          />
        )}
      </AnimatePresence>

      {/* Workout View Modal */}
      <AnimatePresence>
        {viewingWorkout && (
          <WorkoutViewModal
            workout={viewingWorkout}
            onClose={() => setViewingWorkout(null)}
            onEdit={() => { setViewingWorkout(null); setEditingWorkout(viewingWorkout); setShowForm(true); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showForm && (
          <WorkoutForm
            workout={editingWorkout}
            onClose={() => { setShowForm(false); setEditingWorkout(null); }}
            onSave={() => { setShowForm(false); setEditingWorkout(null); fetchWorkouts(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const WorkoutCard = ({ workout, selected, onToggleSelect, onView, onEdit, onDuplicate, onDelete }) => {
  const exerciseCount = workout.sections?.reduce((acc, s) => acc + (s.exercises?.length || 0), 0) || 0;
  
  return (
    <div className={`bg-[#141B2D] border rounded-xl overflow-hidden hover:border-primary/50 transition-all ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} data-testid={`workout-card-${workout.workout_id}`}>
      <div className="aspect-video bg-[#1E293B] relative">
        {workout.thumbnail_url ? (
          <img src={workout.thumbnail_url} alt={workout.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layers className="w-12 h-12 text-gray-500" />
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span className="text-xs">{workout.estimated_duration} min</span>
        </div>
        {/* Selection Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center transition-colors ${
            selected ? 'bg-primary text-white' : 'bg-black/60 hover:bg-black/80 text-white'
          }`}
          data-testid={`select-workout-${workout.workout_id}`}
        >
          {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-heading text-lg font-bold">{workout.title}</h3>
          <div className="flex gap-1">
            <button 
              onClick={onView} 
              className="p-1 hover:bg-[#1E293B] rounded" 
              title="View workout"
              data-testid={`view-workout-${workout.workout_id}`}
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              onClick={onDuplicate} 
              className="p-1 hover:bg-[#1E293B] rounded text-lime-300" 
              title="Duplicate workout"
              data-testid={`duplicate-workout-${workout.workout_id}`}
            >
              <Copy className="w-4 h-4" />
            </button>
            <button 
              onClick={onEdit} 
              className="p-1 hover:bg-[#1E293B] rounded"
              title="Edit workout"
              data-testid={`edit-workout-${workout.workout_id}`}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button 
              onClick={onDelete} 
              className="p-1 hover:bg-[#1E293B] rounded text-red-400"
              title="Delete workout"
              data-testid={`delete-workout-${workout.workout_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{workout.description}</p>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
            {workout.category}
          </span>
          <span className="text-xs bg-lime-400/20 text-lime-300 px-2 py-1 rounded">
            {exerciseCount} exercises
          </span>
          <span className="text-xs bg-[#1E293B] text-gray-400 px-2 py-1 rounded capitalize">
            {workout.tier_required === 'just_for_me' ? 'Personal' : workout.tier_required}
          </span>
        </div>
      </div>
    </div>
  );
};

// Workout List Item (for list view)
const WorkoutListItem = ({ workout, selected, onToggleSelect, onView, onEdit, onDuplicate, onDelete }) => {
  const exerciseCount = workout.sections?.reduce((acc, s) => acc + (s.exercises?.length || 0), 0) || 0;
  
  return (
    <div className={`bg-[#141B2D] border rounded-lg p-4 hover:border-primary/50 transition-all flex items-center gap-4 ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} data-testid={`workout-list-${workout.workout_id}`}>
      {/* Selection Checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0 ${
          selected ? 'bg-primary text-white' : 'bg-[#1E293B] hover:bg-[#2D3B4F] text-white'
        }`}
        data-testid={`select-workout-list-${workout.workout_id}`}
      >
        {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
      </button>
      
      {/* Thumbnail */}
      <div className="w-20 h-14 bg-[#1E293B] rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
        {workout.thumbnail_url ? (
          <img src={workout.thumbnail_url} alt={workout.title} className="w-full h-full object-cover" />
        ) : (
          <Layers className="w-6 h-6 text-gray-500" />
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white truncate mb-1">{workout.title}</h3>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded capitalize">
            {workout.category}
          </span>
          <span className="text-xs bg-lime-400/20 text-lime-300 px-2 py-0.5 rounded">
            {exerciseCount} exercises
          </span>
          <span className="text-xs bg-[#2D3B4F] text-gray-400 px-2 py-0.5 rounded flex items-center gap-1">
            <Clock className="w-3 h-3" /> {workout.estimated_duration} min
          </span>
          {workout.times_per_week && (
            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
              {workout.times_per_week}x/wk
            </span>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button 
          onClick={onView} 
          className="p-2 hover:bg-[#1E293B] rounded-lg"
          title="View workout"
          data-testid={`view-workout-list-${workout.workout_id}`}
        >
          <Eye className="w-4 h-4" />
        </button>
        <button 
          onClick={onDuplicate} 
          className="p-2 hover:bg-[#1E293B] rounded-lg text-lime-300"
          title="Duplicate workout"
          data-testid={`duplicate-workout-list-${workout.workout_id}`}
        >
          <Copy className="w-4 h-4" />
        </button>
        <button 
          onClick={onEdit} 
          className="p-2 hover:bg-[#1E293B] rounded-lg"
          title="Edit workout"
          data-testid={`edit-workout-list-${workout.workout_id}`}
        >
          <Edit className="w-4 h-4" />
        </button>
        <button 
          onClick={onDelete} 
          className="p-2 hover:bg-[#1E293B] rounded-lg text-red-400"
          title="Delete workout"
          data-testid={`delete-workout-list-${workout.workout_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ==================== WORKOUT VIEW MODAL ====================
const WorkoutViewModal = ({ workout, onClose, onEdit }) => {
  const exerciseCount = workout.sections?.reduce((acc, s) => acc + (s.exercises?.length || 0), 0) || 0;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="workout-view-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#141B2D] border-b border-[#1E293B] p-6 flex justify-between items-start">
          <div>
            <h2 className="font-heading text-2xl font-bold">{workout.title}</h2>
            <p className="text-gray-400 mt-1">{workout.description}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg" data-testid="close-workout-view">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Workout Info */}
        <div className="p-6 border-b border-[#1E293B]">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-[#141B2D] px-4 py-2 rounded-lg">
              <Clock className="w-5 h-5 text-primary" />
              <span className="font-medium">{workout.estimated_duration} min</span>
            </div>
            <div className="flex items-center gap-2 bg-[#141B2D] px-4 py-2 rounded-lg">
              <Layers className="w-5 h-5 text-lime-300" />
              <span className="font-medium">{exerciseCount} exercises</span>
            </div>
            <div className="flex items-center gap-2 bg-[#141B2D] px-4 py-2 rounded-lg">
              <Target className="w-5 h-5 text-emerald-400" />
              <span className="font-medium">{workout.times_per_week ? `${workout.times_per_week}x/week` : "—"}</span>
            </div>
            <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-lg">
              <span className="font-medium capitalize text-primary">{workout.category}</span>
            </div>
            <div className="flex items-center gap-2 bg-[#141B2D] px-4 py-2 rounded-lg">
              <Crown className="w-5 h-5 text-amber-400" />
              <span className="font-medium capitalize">{workout.tier_required === 'just_for_me' ? 'Personal' : workout.tier_required}</span>
            </div>
          </div>
        </div>

        {/* Sections/Blocks */}
        <div className="p-6 max-h-[50vh] overflow-y-auto">
          {workout.sections && workout.sections.length > 0 ? (
            <div className="space-y-6">
              {workout.sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                      <span className="font-heading font-bold text-primary text-lg">
                        {String.fromCharCode(65 + sectionIndex)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{section.name || `Block ${String.fromCharCode(65 + sectionIndex)}`}</h3>
                      <p className="text-xs text-gray-500 capitalize">
                        {section.section_type || 'main'} 
                        {section.rounds > 1 && ` • ${section.rounds} rounds`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Exercises in section */}
                  <div className="space-y-2">
                    {section.exercises?.map((exercise, exIndex) => (
                      <div 
                        key={exIndex} 
                        className="flex items-center gap-4 bg-[#141B2D] rounded-lg p-3"
                      >
                        <div className="w-8 h-8 bg-[#2D3B4F] rounded-lg flex items-center justify-center text-sm font-bold">
                          {exIndex + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">{exercise.exercise_name || 'Unknown Exercise'}</p>
                          <p className="text-sm text-gray-500">
                            {exercise.sets} sets × {exercise.reps} reps
                            {exercise.rest_seconds && ` • ${exercise.rest_seconds}s rest`}
                            {exercise.tempo && ` • Tempo: ${exercise.tempo}`}
                          </p>
                          {exercise.notes && (
                            <p className="text-xs text-gray-400 mt-1 italic">{exercise.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!section.exercises || section.exercises.length === 0) && (
                      <p className="text-gray-500 text-sm py-2">No exercises in this block</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Layers className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No blocks or exercises defined for this workout</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E293B] p-6 flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
            data-testid="close-workout-view-btn"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
            data-testid="edit-workout-from-view-btn"
          >
            <Edit className="w-5 h-5" /> Edit Workout
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WorkoutLibrary;
